/**
 * Customers API Module
 * Handles all customer-related API calls
 */

import apiClient, { extractData } from '../api-client';

export const customerApi = {
  /**
   * List all customers with pagination
   * @param {Object} params - Query parameters
   * @param {number} params.page - Page number (default: 1)
   * @param {number} params.size - Page size (default: 20, max: 100)
   * @param {string} params.search - Search term
   * @param {string} params.sort_by - Sort field (default: created_at)
   * @param {string} params.sort_order - Sort order (default: desc)
   * @returns {Promise<Object>} Paginated response with customers
   */
  list(params) {
    return apiClient.get('/customers', { params }).then(extractData);
  },

  /**
   * Get customer by ID
   * @param {number} customerId - Customer ID
   * @returns {Promise<Object>} Customer response
   */
  getById(customerId) {
    return apiClient.get(`/customers/${customerId}`).then(extractData);
  },

  /**
   * Create a new customer
   * @param {Object} customerData - Customer data
   * @param {string} customerData.name - Customer name (required)
   * @param {string} customerData.phone - Phone number
   * @param {string} customerData.address - Address
   * @param {string} customerData.zalo_account_id - Zalo account ID
   * @param {string} customerData.notes - Notes
   * @param {Array} customerData.zalo_groups - Zalo groups array
   * @returns {Promise<Object>} Created customer response
   */
  create(customerData) {
    return apiClient.post('/customers', customerData).then(extractData);
  },

  /**
   * Update customer
   * @param {number} customerId - Customer ID
   * @param {Object} customerData - Customer data to update
   * @returns {Promise<Object>} Updated customer response
   */
  update(customerId, customerData) {
    return apiClient.put(`/customers/${customerId}`, customerData).then(extractData);
  },

  /**
   * Delete customer
   * @param {number} customerId - Customer ID
   * @returns {Promise<Object>} Message response
   */
  delete(customerId) {
    return apiClient.delete(`/customers/${customerId}`).then(extractData);
  },

  /**
   * Add Zalo group to customer
   * @param {number} customerId - Customer ID
   * @param {Object} groupData - Zalo group data
   * @param {string} groupData.zalo_group_id - Zalo group ID (required)
   * @param {string} groupData.group_name - Group name
   * @returns {Promise<Object>} Created group response
   */
  addZaloGroup(customerId, groupData) {
    return apiClient.post(`/customers/${customerId}/zalo-groups`, groupData).then(extractData);
  },

  /**
   * Remove Zalo group from customer
   * @param {number} customerId - Customer ID
   * @param {number} groupId - Zalo group ID
   * @returns {Promise<Object>} Message response
   */
  removeZaloGroup(customerId, groupId) {
    return apiClient.delete(`/customers/${customerId}/zalo-groups/${groupId}`).then(extractData);
  },

  /**
   * Nhân viên (staff đang hoạt động) chọn được trong popup chia sẻ khách — chỉ admin
   * @returns {Promise<Array>} [{ id, full_name, username, is_active }]
   */
  shareCandidates() {
    return apiClient.get('/customers/share-candidates').then(extractData);
  },

  /**
   * Nhân viên đang được chia sẻ khách — chỉ admin
   * @param {number} customerId - Customer ID
   * @returns {Promise<Array>} [{ id, full_name, username, is_active }]
   */
  getShares(customerId) {
    return apiClient.get(`/customers/${customerId}/shares`).then(extractData);
  },

  /**
   * Thay CẢ tập nhân viên được chia sẻ khách (bỏ khỏi danh sách = thu hồi) — chỉ admin
   * @param {number} customerId - Customer ID
   * @param {Array<number>} userIds - Id nhân viên
   * @returns {Promise<Array>} Tập mới
   */
  updateShares(customerId, userIds) {
    return apiClient.put(`/customers/${customerId}/shares`, { user_ids: userIds }).then(extractData);
  }
};
